//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnisonColor } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';
import { Placement } from 'tippy.js';

import Icon from '@components/atoms/icon/Icon';
import { SmallTooltipForIcon } from '@components/molecules/tooltip/Tooltip';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  alertColor?: UnisonColor;
  alertText: string | React.ReactElement;
  placement?: Placement;
}

/**
 * The Alert atom renders a standardized 16x16 px alarm icon
 * with a standardized tooltip that appears on hover.
 */
export const AlertIcon = ({
  alertColor,
  alertText,
  className = '',
  placement,
}: Props) => {
  return (
    <div className={className}>
      <SmallTooltipForIcon
        content={alertText}
        placement={placement}
        target={<Alarm $alertColor={alertColor} />}
      />
    </div>
  );
};

const Alarm = styled(Icon).attrs({
  name: 'alarm',
})<{ $alertColor?: UnisonColor }>(
  ({ $alertColor, theme }) => `
  color: ${$alertColor ?? theme.colors.aliases.warning}
`,
);

/**
 * This style adds a left margin of 8px. Because this style is a formal design consideration,
 * we're exporting it for the sake of consistency and usability.
 */
export const AlertIconWithMargin = styled(AlertIcon)(
  ({ theme }) => `
  margin-left: ${theme.spacings[2]};
`,
);

export default AlertIcon;
